import { VGS_FIELD_ERRORS } from '../constants/VgsFormConstants';
import getCardCodeFromVGSCardType from './getCardCodeFromVGSCardType';

/**
 * Creates an empty errors object for VGS form validation
 * @returns {Object} Empty errors object with all fields set to empty strings
 */
export const createBaseErrors = () => ({
  cardNumber: '',
  expiration: '',
  cardHolderName: '',
  cvc: '',
  address_line1: '',
  address_line2: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  address_country: '',
});

/**
 * Evaluates the VGS form state and returns validation errors
 * @param {Object} state - The VGS form state
 * @param {Object} options - Configuration options
 * @param {boolean} options.doShowSecurityCodeInput - Whether to validate CVC field
 * @param {boolean} options.doTakeCardAddressInputs - Whether to validate address fields
 * @param {Object} options.acceptedPaymentCards - Accepted payment card codes
 * @param {Object} options.currentCreditCard - Current credit card data
 * @param {Array} options.availableStates - Available states for address validation
 * @returns {Object} Object containing errors and general error message
 */
export const evaluateVgsFormState = (state, options = {}) => {
  const {
    doShowSecurityCodeInput = true,
    doTakeCardAddressInputs = true,
    acceptedPaymentCards = {},
    currentCreditCard = null,
    availableStates = [],
  } = options;

  if (!state) {
    return { errors: createBaseErrors(), generalError: '' };
  }

  const newErrors = createBaseErrors();
  const { cardNumber: card, expiration: exp, cardHolderName: name, cvc } = state;

  // Validate card fields
  if (card && !card.isValid && card.isDirty) {
    newErrors.cardNumber = VGS_FIELD_ERRORS.cardNumber;
  }
  if (exp && !exp.isValid && exp.isDirty) {
    newErrors.expiration = VGS_FIELD_ERRORS.expiration;
  }
  if (name && !name.isValid && name.isDirty) {
    newErrors.cardHolderName = VGS_FIELD_ERRORS.cardHolderName;
  }
  if (doShowSecurityCodeInput && cvc && !cvc.isValid && (cvc.isDirty || cvc.isEmpty)) {
    newErrors.cvc = VGS_FIELD_ERRORS.cvc;
  }

  // Check card acceptance
  const currentAcceptedCardCodes = Object.keys(acceptedPaymentCards);
  const currentAcceptedCardValues = Object.values(acceptedPaymentCards);
  let cardAcceptanceError = '';

  if (card && card.isValid && card.cardType && currentAcceptedCardCodes.length > 0) {
    const cardCode = getCardCodeFromVGSCardType(card.cardType);
    if (cardCode && !currentAcceptedCardCodes.includes(cardCode)) {
      const cardBrand = card.cardType || 'card';
      cardAcceptanceError = `${cardBrand} card isn't accepted by the hotel. Please use one of these cards: ${currentAcceptedCardValues.join(', ')}`;
      newErrors.cardNumber = cardAcceptanceError;
    }
  }

  // Validate address fields
  if (doTakeCardAddressInputs && currentCreditCard && state.address) {
    if (!currentCreditCard.addressLineOne?.trim()) {
      newErrors.address_line1 = 'Address line 1 required';
    }
    if (!currentCreditCard.city?.trim()) {
      newErrors.address_city = 'City required';
    }
    if (!currentCreditCard.billingCode?.trim()) {
      newErrors.address_zip = 'ZIP/Postal code required';
    }
    if (!currentCreditCard.country?.trim()) {
      newErrors.address_country = 'Country required';
    }

    if (availableStates && availableStates.length > 0) {
      const selectedState = availableStates.find((s) => s.code === currentCreditCard.state);
      if (!selectedState || !selectedState.code?.trim()) {
        newErrors.address_state = 'State/Province required';
      }
    }
  }

  return { errors: newErrors, generalError: cardAcceptanceError };
};

export default evaluateVgsFormState;
